import { CommonModule } from "@angular/common";
import { AfterViewInit, Component, computed, ElementRef, inject, ViewChild } from "@angular/core";
import { takeUntilDestroyed, toObservable, toSignal } from "@angular/core/rxjs-interop";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { AccessLevel, AccountSubType, AccountType } from "@front/m19-api-client";
import { AccountSelectionService, UserSelectionService } from "@front/m19-services";
import { IButtonComponent } from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { AccountSelectorComponent } from "@m19-board/account-selector/account-selector.component";
import { MultiAccountSelectorComponent } from "@m19-board/account-selector/multi-account-selector.component";
import { CurrencySelectionComponent } from "@m19-board/currency-selector/currency-selection.component";
import { DateRangeSelectorComponent } from "@m19-board/date-range-selector/date-range-selector.component";
import { AccountSettingsService } from "@m19-board/settings/account-settings.service";
import { M19RouteData } from "@m19-board/utils/M19Route";
import { LayoutSelectorService } from "@m19-board/layout-selector.service";
import { LoadingService } from "@m19-board/services/loading.service";
import { PageLayoutService } from "@m19-board/containers/page-layout/page-layout.service";
import { switchMap } from "rxjs/operators";
import { map } from "rxjs";
import { BreadcrumbItemComponent } from "@m19-board/shared/breadcrumb-item/breadcrumb-item.component";

@Component({
  selector: "page-layout",
  standalone: true,
  imports: [
    CommonModule,
    DateRangeSelectorComponent,
    CurrencySelectionComponent,
    MultiAccountSelectorComponent,
    IButtonComponent,
    TranslocoDirective,
    AccountSelectorComponent,
    BreadcrumbItemComponent,
  ],
  templateUrl: "./page-layout.component.html",
})
export class PageLayoutComponent implements AfterViewInit {
  private readonly userSelectionService = inject(UserSelectionService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly accountSettingsService = inject(AccountSettingsService);
  private readonly translocoService = inject(TranslocoService);
  private readonly router = inject(Router);
  private readonly titleService = inject(Title);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly layoutSelectorService = inject(LayoutSelectorService);
  private readonly loadingService = inject(LoadingService);
  private readonly pageLayoutService = inject(PageLayoutService);

  private routeData$ = this.pageLayoutService.navigationEvents$.pipe(
    map(() => this.activatedRoute),
    switchMap((route) => {
      while (route.firstChild) {
        route = route.firstChild;
      }
      return route.data;
    }),
  );
  private routeData = toSignal<M19RouteData>(this.routeData$);
  parentTitle = computed<string>(() => this.translocoService.translate(this.routeData()?.parentTitle ?? ""));
  parentRouteUrl = computed(() => this.routeData()?.parentRouteUrl);
  title = computed(() => this.routeData()?.title);
  private title$ = toObservable(this.parentTitle);

  withDatePicker = computed(() => this.routeData()?.withDatePicker ?? false);
  withCurrencySelector = computed(() => this.routeData()?.withCurrencySelector ?? false);
  withAccountSelector = computed(() => this.routeData()?.withAccountSelector ?? false);
  withMultiAccountSelector = computed(() => this.routeData()?.withMultiAccountSelector ?? false);
  withChatbotSafeMargin = computed(() => this.routeData()?.withChatbotSafeMargin ?? false);
  subPage = computed(() => this.routeData()?.subPage ?? false);

  isSidebarCollapsed = this.userSelectionService.sidebarCollapsed;
  loading = this.loadingService.loading;

  private readonly accountMarketplaces = toSignal(this.accountSelectionService.accountGroupSelection$);
  invalidAccountMarketplaces = computed(
    () =>
      this.accountMarketplaces()
        ?.getAccountMarketplaces()
        .filter((am) => am.accessLevel != AccessLevel.READ && !am.isValidToken) ?? [],
  );
  pendingValidationAccountMarketplaces = computed(
    () =>
      this.accountMarketplaces()
        ?.getAccountMarketplaces()
        .filter(
          (am) =>
            am.accessLevel != AccessLevel.READ &&
            am.accountType == AccountType.VENDOR &&
            am.isValidToken &&
            !am.approvedBy &&
            (am.nbAsinsToValidate ?? 0) > 0,
        ) ?? [],
  );

  @ViewChild("breadcrumbPortalOutlet") breadcrumbPortalOutlet!: ElementRef<HTMLElement>;

  constructor() {
    this.title$
      .pipe(takeUntilDestroyed())
      .subscribe((title) =>
        this.titleService.setTitle(this.layoutSelectorService.getAgencyName() + (title ? " - " + title : "")),
      );
  }

  ngAfterViewInit() {
    this.pageLayoutService.registerPortalOutlet(this.breadcrumbPortalOutlet.nativeElement);
  }

  requestGrant() {
    if (this.invalidAccountMarketplaces().length == 1) {
      const am = this.invalidAccountMarketplaces()[0];
      this.accountSettingsService.grantSellingPartnerAccess(
        am.accountId,
        am.marketplace,
        am.accountType!,
        am.accountSubType === AccountSubType.KDP_AUTHOR,
      );
    } else {
      // TODO: expand account groups with invalid access
      this.router.navigate(["accounts"]);
    }
  }

  validateAccountIntegration() {
    // display popup for first account requiring validation
    if (this.pendingValidationAccountMarketplaces().length == 0) {
      return;
    }
    this.accountSettingsService.validateIntegration(this.pendingValidationAccountMarketplaces()[0]);
  }
}
